import { Box, Typography, colors, lighten, useTheme } from "@mui/material";
import { ThemeColor, UserReport } from "../../system/Domain";
import useCompanies from "../../system/useCompanies";
import { buildUserReportParameters } from "./userReportParameterFormatting";

interface Props {
	userReport: UserReport;
}

// Displays the parameters a UserReport was requested with as "Name: value" lines. Toggled via a
// left-side ListItemAction button in UserReportListItem.tsx, following the same mode-switch
// pattern as UserTile.tsx (e.g. its "Rollen" chip -> role list). Shares the same
// parameter/color/importance data (buildUserReportParameters) as the condensed chips
// (UserReportParameterChips.tsx).
export default ({ userReport }: Props) => {
	const theme = useTheme();
	const [companies] = useCompanies();

	const parameters = buildUserReportParameters(userReport, { companies: companies ?? [] });

	// Mirrors ListItemChip's color mapping (see ListItemChip.tsx) so both approaches highlight
	// parameters identically - "info" is the grey/default look, everything else uses the theme
	// palette's ".light" shade.
	const colorFor = (color: ThemeColor) => {
		switch (color) {
			case "success":
				return theme.palette.success.light;
			case "warning":
				return theme.palette.warning.light;
			case "error":
				return theme.palette.error.light;
			case "info":
				return lighten(colors.grey[500], 0.3);
			default:
				return theme.palette.primary.light;
		}
	};

	if (parameters.length === 0) {
		return (
			<Box mt={3} mb={1}>
				<Typography variant="caption">Keine Parameter</Typography>
			</Box>
		);
	}

	return (
		<Box mt={3} mb={1}>
			{parameters.map((parameter) => (
				<Box key={parameter.key} color={colorFor(parameter.color)}>
					<Typography variant="caption" sx={{ fontWeight: parameter.important ? 600 : 400 }}>
						{parameter.name}: {parameter.value}
					</Typography>
				</Box>
			))}
		</Box>
	);
};
